<template>
  <div class="page-view">
    <div class="page-toolbar">
      <el-button type="primary" @click="addTask()">Add</el-button>
      <el-input
        v-model="searchQuery"
        class="page-search"
        placeholder="搜索 wechat user / target / cron / content"
        clearable
        size="small"
      />
      <el-pagination
        v-model:current-page="pageNum"
        v-model:page-size="pageSize"
        :total="filteredData.length"
        :page-sizes="[20, 50, 100]"
        :layout="paginationLayout"
        :small="isMobile"
        :pager-count="isMobile ? 5 : 7"
      />
    </div>
    <div class="table-wrap" :style="tableWrapStyle">
      <el-auto-resizer>
        <template #default="{ height, width }">
          <el-table-v2 :columns="columns" :data="paginatedData" :width="width" :height="height" fixed />
        </template>
      </el-auto-resizer>
    </div>
  </div>
  <el-dialog v-model="detailDialogVisible" title="Task Detail" width="800px" :close-on-click-modal="false"
    :close-on-press-escape="false" :show-close="false" :align-center="true">
    <el-form ref="formRef" :model="form" :rules="formRules" label-position="top">
      <el-form-item label="微信账号" prop="wechatUser">
        <el-input v-model="form.wechatUser" />
      </el-form-item>
      <el-form-item label="接收人" prop="target">
        <el-input v-model="form.target" />
      </el-form-item>
      <el-form-item label="定时(Cron)" prop="cron">
        <el-input v-model="form.cron" placeholder="e.g. 0 9 * * 1-5" />
      </el-form-item>
      <el-form-item label="内容" prop="content">
        <el-input v-model="form.content" :rows="10" type="textarea" />
      </el-form-item>
      <el-form-item label="@">
        <el-input v-model="form.at" />
      </el-form-item>
    </el-form>
    <template #footer>
      <span class="dialog-footer">
        <el-button :disabled="saving || deleting" @click="detailDialogVisible = false">取消</el-button>
        <el-button type="danger" :loading="deleting" :disabled="saving || !taskId" @click="del()">删除</el-button>
        <el-button type="primary" :loading="saving" :disabled="deleting" @click="save()">保存</el-button>
      </span>
    </template>
  </el-dialog>
</template>

<script setup lang="tsx">
import { computed, reactive, ref, watch } from "vue";
import type { FormInstance, FormRules } from "element-plus";
import { getTasks, saveTask, deleteTask } from "../api/http";
import { ElButton, ElMessage, ElMessageBox } from "element-plus";
import type { Task } from "../api/models";
import { useBreakpoint } from "../composables/useBreakpoint";

const { isMobile } = useBreakpoint();

const detailDialogVisible = ref(false);
const form = reactive({
  wechatUser: "",
  target: "",
  cron: "",
  content: "",
  at: "",
});
const formRef = ref<FormInstance>();
const cronPattern = /^\s*\S+(?:\s+\S+){4,5}\s*$/;
const formRules: FormRules = {
  wechatUser: [{ required: true, message: "Wechat user is required", trigger: "blur" }],
  target: [{ required: true, message: "Target receiver is required", trigger: "blur" }],
  cron: [
    { required: true, message: "Cron expression is required", trigger: "blur" },
    {
      validator: (_rule, value: string, callback) => {
        if (!value || cronPattern.test(value)) {
          callback();
        } else {
          callback(new Error("Cron expression must have 5 or 6 fields"));
        }
      },
      trigger: "blur",
    },
  ],
  content: [{ required: true, message: "Message content is required", trigger: "blur" }],
};
const data = ref<Task[]>([]);
const saving = ref(false);
const deleting = ref(false);

const searchQuery = ref("");
const filteredData = computed(() => {
  const q = searchQuery.value.trim().toLowerCase();
  if (!q) return data.value;
  return data.value.filter((t) =>
    (t.wechatUserId ?? "").toLowerCase().includes(q) ||
    (t.target ?? "").toLowerCase().includes(q) ||
    (t.cron ?? "").toLowerCase().includes(q) ||
    (t.content ?? "").toLowerCase().includes(q) ||
    (t.at ?? "").toLowerCase().includes(q)
  );
});

const pageNum = ref(1);
const pageSize = ref(20);
const paginatedData = computed(() => {
  const start = (pageNum.value - 1) * pageSize.value;
  return filteredData.value.slice(start, start + pageSize.value);
});
const paginationLayout = computed(() => (isMobile.value ? "prev, pager, next" : "total, sizes, prev, pager, next, jumper"));

const tableWrapStyle = computed(() =>
  isMobile.value
    ? { height: 50 + Math.max(1, paginatedData.value.length) * 50 + "px", flex: "none", minHeight: "0" }
    : undefined
);

watch(searchQuery, () => { pageNum.value = 1; });

const detailCellRenderer = ({ rowData: row }: { rowData: any }) => (
  <>
    <ElButton size="small" onClick={() => { detail(row); }}>详情</ElButton>
  </>
);

const desktopColumns = [
  { key: "order", dataKey: "order", title: "#", width: 60, align: "center" },
  { key: "wechatUserId", dataKey: "wechatUserId", title: "Wechat User", width: 160 },
  { key: "target", dataKey: "target", title: "Target", width: 220, flexGrow: 1 },
  { key: "cron", dataKey: "cron", title: "Cron", width: 120 },
  { key: "content", dataKey: "content", title: "Content", width: 280, flexGrow: 1 },
  { key: "at", dataKey: "at", title: "@", width: 140 },
  { key: "detail", dataKey: "_id", title: "Detail", width: 100, align: "center", fixed: "right" as const, cellRenderer: detailCellRenderer },
];

const mobileColumns = [
  { key: "wechatUserId", dataKey: "wechatUserId", title: "Wechat User", width: 100 },
  { key: "target", dataKey: "target", title: "Target", width: 100, flexGrow: 1 },
  { key: "detail", dataKey: "_id", title: "", width: 70, align: "center", fixed: "right" as const, cellRenderer: detailCellRenderer },
];

const columns = computed(() => (isMobile.value ? mobileColumns : desktopColumns));

const loadTask = () => {
  getTasks().then(res => {
    if (res.successful) {
      res.result.forEach((item, index) => {
        item.order = index + 1;
      });
      data.value = res.result;
    } else {
      ElMessage({
        message: res.message,
        type: "error",
      });
    }
  });
}

let taskId = "";

const detail = (row: Task) => {
  taskId = row._id;
  form.wechatUser = row.wechatUserId;
  form.target = row.target;
  form.cron = row.cron;
  form.content = row.content;
  form.at = row.at;
  formRef.value?.clearValidate();
  detailDialogVisible.value = true;
}

const del = () => {
  ElMessageBox.confirm(
    "删除后任务就会停止，确定删除吗？",
    "Warning",
    {
      confirmButtonText: 'OK',
      cancelButtonText: 'Cancel',
      type: 'warning',
    }
  ).then(() => {
    deleting.value = true;
    deleteTask(taskId).then(res => {
      if (res.successful) {
        ElMessage.success("delete successful");
        loadTask();
        detailDialogVisible.value = false;
      } else {
        ElMessage.error(res.message ?? "delete failed");
      }
    }).catch(() => {
      ElMessage.error("delete failed");
    }).finally(() => {
      deleting.value = false;
    });
  }).catch(() => {
    ElMessage({
      type: 'info',
      message: 'Delete canceled',
    })
  });
}

const save = async () => {
  if (!formRef.value) return;
  try {
    await formRef.value.validate();
  } catch {
    return;
  }
  saving.value = true;
  saveTask(taskId, form.wechatUser, form.target, form.cron, form.content, form.at).then(res => {
    if (res.successful) {
      ElMessage.success("save successful");
      detailDialogVisible.value = false;
      loadTask();
    } else {
      ElMessage.error(res.message ?? "save failed");
    }
  }).catch(() => {
    ElMessage.error("save failed");
  }).finally(() => {
    saving.value = false;
  });
}

const addTask = () => {
  taskId = "";
  form.wechatUser = "";
  form.target = "";
  form.cron = "";
  form.content = "";
  form.at = "";
  formRef.value?.clearValidate();
  detailDialogVisible.value = true;
}

loadTask();
</script>

<style scoped>
.row {
  line-height: 30px;
}

.caption {
  text-align: right;
}
</style>